import {useMemo, type MouseEvent} from "react";
import {Link} from "react-router-dom";
import type {DibDocument} from "../types";
import {buildTocTree, type TocEntry} from "../tocUtils";
import {buildTopicUrl} from "../contentUtils";
import classes from "./TableOfContentsLEGACY.module.css";

type Props = {
    document: DibDocument;
    activeId: string | null;
    onTocClick: (id: string) => void;
};

export const TableOfContentsLEGACY = ({
    document: doc,
    activeId,
    onTocClick,
}: Props) => {
    const entries = useMemo(() => buildTocTree(doc), [doc]);

    const expandedIds = useMemo(() => {
        if (!activeId) return new Set<string>();
        const ancestors = findAncestors(entries, activeId) ?? [];
        return new Set([...ancestors, activeId]);
    }, [entries, activeId]);

    if (entries.length === 0) return null;

    return (
        <ul className={classes.list}>
            {entries.map((entry) => (
                <TocItem
                    key={entry.id}
                    entry={entry}
                    topictypeId={doc.topictypeId}
                    name={doc.name}
                    topicId={doc.topicId}
                    activeId={activeId}
                    expandedIds={expandedIds}
                    onTocClick={onTocClick}
                />
            ))}
        </ul>
    );
};

type TocItemProps = {
    entry: TocEntry;
    topictypeId: string;
    name: string;
    topicId: string;
    activeId: string | null;
    expandedIds: Set<string>;
    onTocClick: (id: string) => void;
};

const TocItem = ({
    entry,
    topictypeId,
    name,
    topicId,
    activeId,
    expandedIds,
    onTocClick,
}: TocItemProps) => {
    const isActive = entry.id === activeId;
    const isExpanded = expandedIds.has(entry.id);
    const hasChildren = entry.children.length > 0;
    const isSegment = entry.seg === "1";

    const handleInPageClick = (e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        onTocClick(entry.id);
    };

    const linkClass = isActive
        ? `${classes.link} ${classes.active}`
        : classes.link;

    const link = isSegment ? (
        <Link
            to={`${buildTopicUrl(topictypeId, name, topicId, entry.id)}#${entry.id}`}
            className={linkClass}
            title={entry.title}
        >
            {entry.title}
        </Link>
    ) : (
        <a
            href={`#${entry.id}`}
            className={linkClass}
            onClick={handleInPageClick}
            title={entry.title}
        >
            {entry.title}
        </a>
    );

    return (
        <li>
            {link}
            {hasChildren && isExpanded && (
                <ul className={classes.children}>
                    {entry.children.map((child) => (
                        <TocItem
                            key={child.id}
                            entry={child}
                            topictypeId={topictypeId}
                            name={name}
                            topicId={topicId}
                            activeId={activeId}
                            expandedIds={expandedIds}
                            onTocClick={onTocClick}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
};

// Returns ancestor IDs leading from root down to (but not including) targetId.
// Returns null if targetId is not in this subtree.
const findAncestors = (
    entries: TocEntry[],
    targetId: string
): string[] | null => {
    for (const entry of entries) {
        if (entry.id === targetId) return [];
        const subPath = findAncestors(entry.children, targetId);
        if (subPath !== null) return [entry.id, ...subPath];
    }
    return null;
};
